import {
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  type AlertDialogProps,
  Box,
  Button,
  AlertDialog as ChakraAlertDialog,
  ModalCloseButton,
  type ModalHeaderProps,
  useDisclosure,
} from '@chakra-ui/react';
import { useCallback, useRef } from 'react';
import { create } from 'zustand';

type AlertType = 'confirm' | 'actionless';

interface AlertProps {
  alertType: AlertType;
  onClose?: () => void;
  onAccept?: () => void;
  isOpen?: boolean;
  headerText: string;
  bodyContent: JSX.Element;
  footerAcceptIsDestructive?: boolean;
  footerAcceptText?: string;
  /** Overrides the Cancel button's Chakra color scheme. Defaults to 'navyBlue'. */
  cancelColorScheme?: string;
  buttonLoading?: boolean;
  showCloseBtn?: boolean;
  chakraAlertProps?: Partial<AlertDialogProps>;
  headerProps?: Partial<ModalHeaderProps>;
  skipDefaultDisclosure?: boolean;
}

export type IRevelioAlert = {
  alertType: AlertType;
  isOpen: boolean;
  onClose?: () => void;
  onAccept?: () => void;
  headerText: string;
  bodyContent?: JSX.Element;
  footerAcceptIsDestructive?: boolean;
  footerAcceptText?: string;
  onAlertTriggered?: () => void;
};

const DEFAULT_ACCEPT_TEXT = 'Ok';

interface AlertDialogStore {
  alert: IRevelioAlert | null;
}

const useAlertDialogStore = create<AlertDialogStore>(() => ({
  alert: null,
}));

export function showAlertDialog(alert: IRevelioAlert) {
  useAlertDialogStore.setState({ alert });
  alert.onAlertTriggered?.();
}

export const AlertDialog = ({
  chakraAlertProps = {},
  headerProps = {},
  isOpen,
  onClose,
  skipDefaultDisclosure = false,
  ...props
}: AlertProps) => {
  const cancelRef = useRef(null);
  const storeAlert = useAlertDialogStore((s) => s.alert);

  const alert = skipDefaultDisclosure ? null : storeAlert;

  const alertType = alert?.alertType ?? props.alertType;
  const headerText = alert?.headerText ?? props.headerText;
  const bodyContent = alert?.bodyContent ?? props.bodyContent;
  const footerAcceptIsDestructive =
    alert?.footerAcceptIsDestructive ?? props.footerAcceptIsDestructive;
  const footerAcceptText =
    alert?.footerAcceptText ?? props.footerAcceptText ?? DEFAULT_ACCEPT_TEXT;
  const onAccept = alert?.onAccept ?? props.onAccept;

  const { onClose: disclosureOnClose, ...disclosureMain } = useDisclosure({
    onClose,
    isOpen: alert?.isOpen ?? isOpen,
  });

  const combinedOnClose = useCallback(() => {
    alert?.onClose?.();
    useAlertDialogStore.setState({ alert: null });
    disclosureOnClose();
  }, [alert, disclosureOnClose]);

  return (
    <ChakraAlertDialog
      onClose={combinedOnClose}
      {...chakraAlertProps}
      {...disclosureMain}
      leastDestructiveRef={cancelRef}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" {...headerProps}>
            {headerText}
          </AlertDialogHeader>
          {props.showCloseBtn && <ModalCloseButton />}
          <AlertDialogBody>
            <Box pb={alertType === 'actionless' ? '12px' : '0'}>
              {bodyContent}
            </Box>
          </AlertDialogBody>
          {alertType !== 'actionless' && (
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                colorScheme={props.cancelColorScheme ?? 'navyBlue'}
                variant="ghost"
                onClick={combinedOnClose}
              >
                Cancel
              </Button>
              <Button
                colorScheme={footerAcceptIsDestructive ? 'red' : 'navyBlue'}
                onClick={() => {
                  combinedOnClose();
                  onAccept?.();
                }}
                ml={3}
                isLoading={props.buttonLoading}
              >
                {footerAcceptText}
              </Button>
            </AlertDialogFooter>
          )}
        </AlertDialogContent>
      </AlertDialogOverlay>
    </ChakraAlertDialog>
  );
};

export default AlertDialog;
